import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import Media from 'react-media';
import { Field } from 'formik';
import Yup from 'yup';
import get from 'lodash/get';
import { Alert } from '@lightspeed/flame/Alert';
import { Button } from '@lightspeed/flame/Button';
import Text from '@lightspeed/cirrus-text';
import { entities } from 'pages/store/schema';
import FormField from 'cirrus/FormField';
import CountryDropdown from 'cirrus/Dropdown/Country';
import { getCountryFromCountryName, getL10nForCountryCode } from 'cirrus/Dropdown/Country/utils';
import RegionDropdown from 'cirrus/Dropdown/Region';
import { hasRegions, getRegionFromRegionName } from 'cirrus/Dropdown/Region/utils';
import { formikWithInitialValidation } from 'pages/components/Formik/enhancers';
import Input from 'pages/components/Formik/Input';
import { getErrors } from 'pages/store/selectors';
import { putData } from 'pages/store/actions/data';
import { actionTypes } from 'pages/store/actions/defaults';
import { breakpoints } from 'pages/utils/media';
import Receipt from '../components/Receipt';
import ReceiptAddress from '../components/Receipt/Address';
import ReceiptSaleLinesPlaceholder from '../components/Receipt/SaleLinesPlaceholder';
import FormHeader from '../components/FormHeader';
import PlacesAutocomplete from '../components/PlacesAutocomplete';

const ShopDetailsForm = ({
  errors,
  values,
  setFieldValue,
  setValues,
  handleSubmit,
  isValid,
  isSubmitting,
  loadingErrors,
  savingErrors,
}) => {
  const l10nTranslations = getL10nForCountryCode(values.countryCode).translations;

  return (
    <Fragment>
      <FormHeader
        title="Let's set up your store"
        description="Your location name and address will be visible on your receipts and register. You can change this information later."
      />
      {loadingErrors || savingErrors ? (
        <Alert type="danger" noCloseBtn className="cr-m-2">
          <Text>An error has occurred. Please reload the page and try again</Text>
        </Alert>
      ) : null}
      <div className="cr-flex cr-p-2">
        <form
          onSubmit={handleSubmit}
          method="post"
          className="cr-flex-grow cr-p-2"
          data-test="shop-details-form"
        >
          <div className="cr-mb-3" data-test="shop-name-field-wrapper">
            <Field label="Location name" component={Input} required name="name" type="text" />
          </div>
          <div>
            <div className="cr-mt-1">
              <PlacesAutocomplete values={values} setValues={setValues}>
                <FormField label="Address">
                  <Field
                    component={Input}
                    required
                    name="address1"
                    type="text"
                    placeholder="Street and number"
                    autoComplete="address1"
                    data-test="address-input"
                  />
                </FormField>
              </PlacesAutocomplete>
              <div className="cr-mt-2">
                <Field
                  component={Input}
                  required
                  name="address2"
                  type="text"
                  placeholder="Apartment, suite, etc."
                />
              </div>
            </div>
            <Media query={{ minWidth: breakpoints.md }}>
              {matches => (
                <>
                  <div className="cr-flex cr-flex-wrap">
                    <div className="cr-mt-2 cr-width-full">
                      <Field
                        label="City"
                        component={Input}
                        required
                        name="city"
                        type="text"
                        data-test="city-input"
                      />
                    </div>
                  </div>
                  <div className="cr-flex cr-flex-full">
                    <div className={`cr-mt-2 cr-width-${matches ? '4-12' : '6-12'}`}>
                      {hasRegions(values.countryCode) ? (
                        <FormField
                          label={l10nTranslations.state}
                          status={
                            errors.regionCode || errors.regionName
                              ? {
                                  type: 'error',
                                  message: errors.regionCode || errors.regionName,
                                }
                              : null
                          }
                        >
                          <RegionDropdown
                            inputProps={{ name: 'regionName', autoComplete: 'nope' }}
                            onChange={region => {
                              const { regionCode = '', regionName = '' } = region || {};
                              setValues({
                                ...values,
                                regionCode,
                                regionName,
                              });
                            }}
                            placeholder=""
                            countryCode={values.countryCode}
                            regionName={values.regionName}
                            regionCode={values.regionCode}
                            autosize={false}
                            data-test="region-dropdown"
                          />
                        </FormField>
                      ) : (
                        <Field
                          label={l10nTranslations.state}
                          component={Input}
                          name="regionName"
                          type="text"
                          autoComplete="nope"
                          data-test="region-input"
                        />
                      )}
                    </div>
                    <div className={`cr-mt-2 cr-width-${matches ? '4-12' : '6-12'} cr-pl-2`}>
                      <Field
                        label={l10nTranslations.zip}
                        component={Input}
                        required
                        name="zipcode"
                        type="text"
                        data-test="zip-input"
                      />
                    </div>
                    <div className={`cr-mt-2 cr-width-${matches ? '4-12' : '6-12'} cr-pl-2`}>
                      <FormField
                        label="Country"
                        status={
                          errors.countryCode || errors.countryName
                            ? {
                                type: 'error',
                                message: errors.countryCode || errors.countryName,
                              }
                            : null
                        }
                      >
                        <CountryDropdown
                          clearable={false}
                          inputProps={{ name: 'countryName', autoComplete: 'nope' }}
                          countryName={values.countryName}
                          countryCode={values.countryCode}
                          onSelectChange={option => {
                            let { regionName } = values;
                            let regionCode = '';
                            const { countryCode = '', countryName = '' } = option || {};

                            if (countryCode && hasRegions(countryCode)) {
                              ({ regionCode, regionName } = getRegionFromRegionName({
                                countryCode,
                                regionName: regionCode || regionName,
                              }));
                            }

                            setValues({
                              ...values,
                              countryCode,
                              countryName,
                              regionCode,
                              regionName,
                            });
                          }}
                          onInputChange={value => {
                            setFieldValue('countryName', value);
                          }}
                        />
                      </FormField>
                    </div>
                  </div>
                </>
              )}
            </Media>
          </div>
          <div className="cr-mt-4">
            <Field
              label="Phone"
              component={Input}
              name="phoneNumber"
              type="text"
              placeholder="Enter a phone number"
              required
            />
          </div>
          <div className="cr-mt-4">
            <Field
              label="Email"
              description="Used for sending sales receipts, purchase orders and quotes."
              component={Input}
              name="email"
              type="email"
              placeholder="Enter an email"
              required
            />
          </div>
          <div className="cr-mt-4">
            <Media query={{ minWidth: breakpoints.md }}>
              {matches => (
                <Button
                  variant="primary"
                  block={!matches}
                  fill
                  size={!matches ? 'large' : null}
                  onClick={handleSubmit}
                  loading={isSubmitting}
                  disabled={!isValid}
                  data-test="shop-details-submit-button"
                >
                  Next step
                </Button>
              )}
            </Media>
          </div>
        </form>

        <Media
          query={{ minWidth: breakpoints.lg }}
          render={() => (
            <div className="cr-flex cr-justify-end cr-width-5-12 cr-p-2">
              <Receipt>
                <ReceiptAddress
                  name={values.name}
                  address1={values.address1}
                  address2={values.address2}
                  city={values.city}
                  countryName={values.countryName}
                  zipcode={values.zipcode}
                  phoneNumber={values.phoneNumber}
                />
                <ReceiptSaleLinesPlaceholder />
              </Receipt>
            </div>
          )}
        />
      </div>
    </Fragment>
  );
};

export const enhancedForm = compose(
  formikWithInitialValidation({
    mapPropsToValues: props => {
      let countryCode = get(props, 'data.countryCode', '');
      let countryName = get(props, 'data.countryName', '');
      let regionCode = get(props, 'data.regionCode', '');
      let regionName = get(props, 'data.regionName', '');

      if (countryName && !countryCode) {
        ({ countryCode, countryName } = getCountryFromCountryName(countryName));
      }

      if (countryCode && hasRegions(countryCode)) {
        ({ regionCode, regionName } = getRegionFromRegionName({
          countryCode,
          regionName: regionCode || regionName,
        }));
      }

      return {
        id: get(props, 'data.id', 0),
        name: get(props, 'data.name', ''),
        address1: get(props, 'data.address1', ''),
        address2: get(props, 'data.address2', ''),
        countryCode,
        countryName,
        regionCode,
        regionName,
        city: get(props, 'data.city', ''),
        zipcode: get(props, 'data.zipcode', ''),
        phoneNumber: get(props, 'data.phoneNumber', ''),
        email: get(props, 'data.email', ''),
        lat: get(props, 'data.lat', 0),
        lng: get(props, 'data.lng', 0),
      };
    },
    validationSchema: Yup.object().shape({
      name: Yup.string().required('Location name is required'),
      address1: Yup.string().required('Address is required'),
      address2: Yup.string(),
      countryName: Yup.string().required('Country is required'),
      countryCode: Yup.string().required('Country is required'),
      city: Yup.string().required('City is required'),
      regionName: Yup.string().when('countryCode', (countryCode, schema) => {
        if (countryCode && hasRegions(countryCode)) {
          const { state } = getL10nForCountryCode(countryCode, ['translations.state']).translations;
          return schema.required(`${state} is required`);
        }
        return schema;
      }),
      regionCode: Yup.string(),
      zipcode: Yup.string().when('countryCode', (countryCode, schema) => {
        const { zip } = getL10nForCountryCode(countryCode, ['translations.zip']).translations;
        return schema.required(`${zip} is required`);
      }),
      email: Yup.string()
        .email()
        .required('Email is required'),
      phoneNumber: Yup.string().required('Phone number is required'),
    }),
    handleSubmit: async (values, formikBag) => {
      formikBag.setSubmitting(true);

      if (window.isGriffin()) {
        window.StencilWebAPI.navigation.setBusyState(true);
      }

      try {
        await formikBag.props.putData('/admin/first_start/shop_setup', {
          shopSetup: Object.assign(values, { id: 1 }),
        });
        formikBag.setSubmitting(false);
        formikBag.props.setStep();
      } catch (error) {
        if (error.errors) formikBag.setErrors(error.errors);
        formikBag.setSubmitting(false);
      }

      if (window.isGriffin()) {
        window.StencilWebAPI.navigation.setBusyState(false);
      }
    },
  })
)(ShopDetailsForm);

const mapStateToProps = state => ({
  loadingErrors: getErrors({
    entity: entities.shopDetails,
    list: false,
    actionType: actionTypes.LOADING,
  })(state),
  savingErrors: getErrors({
    entity: entities.shopDetails,
    list: false,
    actionType: actionTypes.SAVING,
  })(state),
});

const mapDispatchToProps = dispatch => ({
  putData: (url, data, onSuccess) =>
    dispatch(putData({ url, payload: data, entity: entities.shopDetails, list: false, onSuccess })),
});

export default connect(mapStateToProps, mapDispatchToProps)(enhancedForm);
